<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from '@/i18n/i18n';
import PersonAvatar from '@/components/Avatar/PersonAvatar.vue';
import { CampaignProspect, CampaignProspectAction } from '@/domains/models/Campaign';
import moment from 'moment/min/moment-with-locales';
import PanelHeaderNav from '@/components/Panel/PanelHeader/PanelHeaderNav.vue';
import DateBadgeWithTooltip from '@/components/Badge/DateBadgeWithTooltip.vue';
import { getMessage } from '@/api/messages';
import BcSpinner from '@/ui-kit/components/BcSpinner/BcSpinner.vue';
import MvIcon from '@/ui-kit/components/MvIcon/MvIcon.vue';

const { t } = useI18n();

type ProspectWithMessages = CampaignProspect & {
  messages: {
    lastSentMessageId?: string;
    customInvitationMessageId?: string;
    receivedMessageId?: string;
  }
}

const props = defineProps<{
  prospect: ProspectWithMessages;
  prospectType: string;
  onClose: () => void;
  onBack: () => void;
}>();

const isLoading = ref(false);
const message = ref();
const noMessage = ref();

// Get the actual message content from the API
const getMessageById = async (messageId?: string) => {
  console.log('messageId', messageId);
  if (!messageId) {
    return '';
  }

  isLoading.value = true;
  try {
    const response = await getMessage(messageId);
    return response.data;
  } catch (error) {
    console.error('Error fetching message:', error);
    return '';
  } finally {
    isLoading.value = false;
  }
};

// Load message content on mount
onMounted(async () => {
  let messageId;
  console.log('props.prospectType', props.prospectType);
  console.log('props.prospect.messages.lastSentMessageId', props.prospect.messages.lastSentMessageId);
  switch (props.prospectType) {
    case 'contacted':
      messageId = props.prospect.messages.lastSentMessageId;
      break;
    case 'accepted':
      messageId = props.prospect.messages.customInvitationMessageId;
      break;
    case 'responded':
      messageId = props.prospect.messages.receivedMessageId;
      break;
  }

  if (messageId) {
    message.value = await getMessageById(messageId);
  } else {
    isLoading.value = false;

    // Set default message when no message ID
    switch (props.prospectType) {
      case 'contacted':
        noMessage.value = t('campaigns.prospects.no-message');
        break;
      case 'accepted':
        noMessage.value = t('campaigns.prospects.no-custom-invitation');
        break;
      case 'responded':
        noMessage.value = t('campaigns.prospects.no-response');
        break;
    }
  }
});

// Get message title based on prospect type
const messageTitle = computed(() => {
  switch (props.prospectType) {
    case 'contacted':
      return t('campaigns.prospects.last-sent-message');
    case 'accepted':
      return t('campaigns.prospects.custom-invitation');
    case 'responded':
      return t('campaigns.prospects.received-message');
    default:
      return '';
  }
});

const isCoachSender = computed(() => message.value?.messageData.is_sender);
const sender = computed(() => isCoachSender.value ? message.value?.coach : message.value?.coder);
const recipient = computed(() => isCoachSender.value ? message.value?.coder : message.value?.coach);


// Format date for display
const formatDate = (date: Date) => {
  return moment(date).format('L LT');
};

// Get the appropriate action date
const messageDate = computed((): Date | string => {
  const messageId = props.prospectType === 'contacted'
    ? props.prospect.messages.lastSentMessageId
    : props.prospectType === 'accepted'
      ? props.prospect.messages.customInvitationMessageId
      : props.prospect.messages.receivedMessageId;

  const relevantAction = props.prospect.prospectActions.find(
    (action: CampaignProspectAction) => action._message === messageId
  );

  return relevantAction?.executedAt || props.prospect.createdAt;
});

// Format the display date
const displayDate = computed(() => {
  return formatDate(messageDate.value);
});
</script>

<template>
  <div class="prospects-panel w-[640px] max-w-[640px] h-[100vh] flex flex-col bg-white">
    <div
      class="flex h-[50px] w-full max-w-[100vw] items-center justify-between gap-6 border-b border-b-[#EDF0FF] bg-neutral-200 px-[18px] py-2">
      <PanelHeaderNav :is-back-visible="true" :title="messageTitle" :on-close="onClose" :on-back="onBack" />
      <h2 class="shrink-0 text-2xl font-bold first-letter:capitalize">
        {{ messageTitle }}
      </h2>
      <DateBadgeWithTooltip :date="props.prospect.createdAt" withHHMM class="h-fit" />
    </div>

    <div class="flex flex-col">
      <div class="flex flex-col gap-4 message-view w-full">

        <!-- Loading indicator -->
        <div v-if="isLoading" class="flex justify-center py-4">
          <BcSpinner size="medium" />
        </div>
        <div v-else-if="message" class="contents">
          <div class="flex flex-col gap-2 mb-4">
            <div class="flex flex-col gap-2 w-full border-b border-blue-200 p-5 text-sm text-blue-900">
              <h3 class="font-semibold">
                <a v-if="!isCoachSender" :href="`${sender.social?.linkedin}`" class="text-blue-linkedin flex items-center gap-[5px]">
                  {{ sender.firstName }} {{ sender.lastName }} <MvIcon name="linkedin-square" size="10" color="blue" />
                </a>
                <span v-else>{{ sender.firstName }} {{ sender.lastName }}</span>
              </h3>
              <h3 class="font-semibold flex gap-1">
                <span>{{ t('generics.to') }} :</span><a v-if="isCoachSender" class="text-blue-linkedin flex items-center gap-[5px]" :href="`${recipient.social?.linkedin}`">{{ recipient.firstName }} {{ recipient.lastName }} <MvIcon name="linkedin-square" size="10" color="blue" />
                </a>
                <span v-else>{{ recipient.firstName }} {{ recipient.lastName }}</span>
              </h3>
            </div>
            <div class="flex flex-col gap-2 p-5 pt-4">
              <p v-if="message.messageData.subject" class="text-blue-900 font-bold">{{ message.messageData.subject }}
              </p>
              <p class="whitespace-pre-wrap text-blue-900">{{ message.messageData.text }}</p>
            </div>
          </div>
        </div>


        <!-- Message content -->
        <div v-if="!message" class="contents">
          <p class="text-blue-900 font-bold">{{ noMessage }}</p>
        </div>
        <div v-else class="contents">

        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.prospects-panel {
  overflow-y: auto;
}

.avatar-placeholder {
  @apply w-10 h-10 rounded-full bg-blue-100;
}
</style>